// src/components/MyDashboard.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import './MyDashboard.css';
import axios from 'axios';
import { API_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import { showToast } from './toast';

Chart.register(...registerables);

const MyDashboard = () => {
  const [data, setData] = useState({
    totalClients: 0,
    totalRevenue: 0,
    averageRevenue: 0,
    thisMonthRevenue: 0,
    highLowRevenue: [],
    performance: [],
  });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchDashboardData = useCallback(async (targetId = '') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const url = targetId
        ? `${API_URL}dashboard/personal?userId=${targetId}`
        : `${API_URL}dashboard/personal`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setData({
          totalClients: response.data.totalClients ?? response.data.subscriber ?? 0,
          totalRevenue: response.data.totalRevenue ?? response.data.totalrevenue ?? 0,
          averageRevenue: response.data.averageRevenue ?? 0,
          thisMonthRevenue: response.data.thisMonthRevenue ?? response.data.monthrevenue ?? 0,
          highLowRevenue: response.data.highLowRevenue || response.data.highlowrevenue || [],
          performance: response.data.performance || [],
        });
      }
    } catch (error) {
      console.error('Error fetching personal dashboard data:', error);
      showToast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch admin user list if current user is admin
  useEffect(() => {
    const checkUserRoleAndFetchUsers = async () => {
      try {
        const userDataStr = localStorage.getItem('user');
        const user = userDataStr ? JSON.parse(userDataStr) : null;
        const adminRole = user?.user_type === 1 || user?.id === 1;
        setIsAdmin(adminRole);

        if (adminRole) {
          const token = localStorage.getItem('authToken');
          const response = await axios.get(`${API_URL}dashboard/personal-users`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (Array.isArray(response.data)) {
            setUsers(response.data);
          }
        }
      } catch (err) {
        console.error('Error checking admin role or fetching users:', err);
      }
    };

    checkUserRoleAndFetchUsers();
  }, []);

  // Fetch dashboard data when selected user changes
  useEffect(() => {
    fetchDashboardData(selectedUserId);
  }, [fetchDashboardData, selectedUserId]);

  // Prepare data for Highest & Lowest Revenue Chart
  const highLowLabels = (data.highLowRevenue || []).map((item) => {
    const monthDate = new Date(item.created_on);
    return isNaN(monthDate.getTime())
      ? item.created_on
      : monthDate.toLocaleString('default', { month: 'short', year: '2-digit' });
  });

  const highRevenueData = (data.highLowRevenue || []).map((item) => Number(item.maxamount || 0));
  const lowRevenueData = (data.highLowRevenue || []).map((item) => Number(item.minamount || 0));

  // Prepare data for Monthly Revenue Chart
  const performanceLabels = (data.performance || []).map((item) => {
    const monthDate = new Date(item.created_on);
    return isNaN(monthDate.getTime())
      ? item.created_on
      : monthDate.toLocaleString('default', { month: 'short', year: '2-digit' });
  });

  const performanceData = (data.performance || []).map((item) => Number(item.amount || 0));

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      <header className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h4>
          My Dashboard <small className="text-muted"> Portfolio</small>
        </h4>

        {isAdmin && (
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="user-select" className="mb-0 fw-semibold text-secondary" style={{ fontSize: '13px' }}>
              Select User:
            </label>
            <select
              id="user-select"
              className="form-select form-select-sm"
              style={{ minWidth: '220px', borderRadius: '6px' }}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">My Performance (Self)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.user_fullname || u.user_name} ({u.user_type === 1 ? 'Admin' : 'Executive'})
                </option>
              ))}
            </select>
          </div>
        )}
      </header>

      {loading && <LoadingSpinner />}

      <div className="">
        <div className="row">
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="widget widget-stats bg-gradient-blue-dark inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Total Clients</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">{Number(data.totalClients || 0).toLocaleString('en-IN')}</div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div className="widget widget-stats bg-gradient-orange inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Total Revenue</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">
                  {Number(data.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div className="widget widget-stats bg-gradient-red inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">Average Revenue</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">
                  {Number(data.averageRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div className="widget widget-stats bg-gradient-green inverse-mode">
              <div className="widget-stats-left">
                <div className="widget-stats-title">This Month Revenue</div>
              </div>
              <div className="widget-stats-right">
                <div className="widget-stats-value">
                  ₹{Number(data.thisMonthRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mainbox mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow chartbox">
              <div className="panel-heading border-0 pb-2">
                <h6 className="title">Highest &amp; Lowest Revenue / Month</h6>
              </div>
              <div className="panel-body">
                {highLowLabels.length === 0 ? (
                  <div className="text-center py-5 text-muted" style={{ minHeight: '200px' }}>
                    <p className="mb-0">No revenue data available to display</p>
                  </div>
                ) : (
                  <Bar
                    data={{
                      labels: highLowLabels,
                      datasets: [
                        {
                          label: 'Highest Revenue ₹',
                          backgroundColor: '#8BC34A',
                          data: highRevenueData,
                        },
                        {
                          label: 'Lowest Revenue ₹',
                          backgroundColor: '#F44336',
                          data: lowRevenueData,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top',
                        },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ₹${Number(ctx.raw || 0).toLocaleString('en-IN')}`,
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6 mainbox mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow chartbox">
              <div className="panel-heading border-0 pb-2">
                <h6 className="title">Monthly Revenue</h6>
              </div>
              <div className="panel-body">
                {performanceLabels.length === 0 ? (
                  <div className="text-center py-5 text-muted" style={{ minHeight: '200px' }}>
                    <p className="mb-0">No monthly revenue data available to display</p>
                  </div>
                ) : (
                  <Bar
                    data={{
                      labels: performanceLabels,
                      datasets: [
                        {
                          label: 'Monthly Revenue ₹',
                          backgroundColor: '#127BBC',
                          data: performanceData,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => `Revenue: ₹${Number(ctx.raw || 0).toLocaleString('en-IN')}`,
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyDashboard;

